import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Switch } from "antd";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomBreadcrumb from "../../common/CustomBreadcrumb";
import { BreadCrum } from "../../common";
import eyeIcon from "../../assets/eye.svg";
import eyeIconClosed from "../../assets/eye-slash.svg";
import {
  validationSchemaAddAdmin,
  validationSchemaManageAdmin,
} from "../../common/Validations";
import {
  ADMIN_PERMISSION_GROUPS,
  getAllPermissionsFromGroups,
  getPermissionGroupTitle,
  getPermissionLabel,
  getPermissionValue,
  mapAdminPermissionGroupsFromApi,
  mapAdminToFormValues,
  buildUpdateAdminPayload,
  mapFormPermissionsToGroupedApi,
  mapStatusToApi,
  MANAGE_ADMIN_PASSWORD_PLACEHOLDER,
} from "./adminUtils";
import {
  createAdminAction,
  createAdminHandler,
} from "../../redux/action/createAdminSlice";
import {
  updateAdminAction,
  updateAdminHandler,
} from "../../redux/action/updateAdminSlice";
import {
  getAdminPermissionsAction,
  getAdminPermissionsHandler,
} from "../../redux/action/getAdminPermissionsSlice";
import { useCommonMessage } from "../../common/CommonMessage";
import Loader from "../Loder/loader";
import "./AdminManagement.css";
import { handleKeyPressSpace } from "../../common/CommonFunctions";

const AddAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const messageApi = useCommonMessage();
  const createAdminSelector = useSelector((state) => state?.createAdmin);
  const updateAdminSelector = useSelector((state) => state?.updateAdmin);
  const getAdminPermissionsSelector = useSelector(
    (state) => state?.getAdminPermissions,
  );
  const isManageMode = location.pathname === "/manage-admin";
  const adminData = location.state?.adminData;
  const [showPassword, setShowPassword] = useState(false);
  const [storedPassword, setStoredPassword] = useState("");
  const [passwordEdited, setPasswordEdited] = useState(false);
  const formikRef = useRef(null);
  const [permissionGroups, setPermissionGroups] = useState(
    ADMIN_PERMISSION_GROUPS,
  );

  useEffect(() => {
    dispatch(getAdminPermissionsHandler());
    return () => {
      dispatch(getAdminPermissionsAction.getAdminPermissionsInfoReset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (getAdminPermissionsSelector?.data) {
      setPermissionGroups(
        mapAdminPermissionGroupsFromApi(getAdminPermissionsSelector.data),
      );
    }
  }, [getAdminPermissionsSelector?.data]);

  useEffect(() => {
    if (getAdminPermissionsSelector?.message) {
      messageApi.open({
        type: "error",
        content: getAdminPermissionsSelector.message,
      });
      dispatch(getAdminPermissionsAction.getAdminPermissionsInfoReset());
    }
  }, [dispatch, getAdminPermissionsSelector?.message, messageApi]);

  useEffect(() => {
    if (isManageMode) {
      dispatch(updateAdminAction.updateAdminInfoReset());
    } else {
      dispatch(createAdminAction.createAdminInfoReset());
    }
  }, [dispatch, isManageMode]);

  useEffect(() => {
    if (isManageMode && !adminData) {
      navigate("/admins");
    }
  }, [adminData, isManageMode, navigate]);

  useEffect(() => {
    setStoredPassword("");
    setPasswordEdited(false);
    setShowPassword(false);
    formikRef.current?.setFieldValue("password", "");
  }, [adminData?.id, isManageMode]);

  const allPermissions = useMemo(
    () => getAllPermissionsFromGroups(permissionGroups),
    [permissionGroups],
  );
  const manageInitialValues = useMemo(
    () => mapAdminToFormValues(adminData),
    [adminData],
  );
  const addInitialValues = useMemo(() => {
    const values = mapAdminToFormValues(adminData, allPermissions);
    return { ...values, status: "Active" };
  }, [adminData, allPermissions]);
  const initialValues = isManageMode ? manageInitialValues : addInitialValues;
  const pageTitle = isManageMode ? "Manage Admin" : "Add Admin";
  const breadcrumbLabel = isManageMode ? "Manage" : "Add Admin";

  const handleCancel = () => {
    navigate("/admins");
  };

  const handleFormSubmit = (values) => {
    const password = isManageMode
      ? passwordEdited
        ? storedPassword.trim()
        : ""
      : values.password?.trim();

    if (isManageMode) {
      dispatch(
        updateAdminHandler(
          buildUpdateAdminPayload(
            { ...values, password },
            adminData?.id,
            permissionGroups,
          ),
        ),
      );
      return;
    }

    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      password,
      permissions: mapFormPermissionsToGroupedApi(
        values.permissions,
        permissionGroups,
      ),
      status: mapStatusToApi(values.status),
    };
    dispatch(createAdminHandler(payload));
  };

  useEffect(() => {
    if (createAdminSelector?.data?.message) {
      messageApi.open({
        type: "success",
        content: createAdminSelector?.data?.message,
      });
      dispatch(createAdminAction.createAdminInfoReset());
      navigate("/admins");
    } else if (createAdminSelector?.message) {
      messageApi.open({
        type: "error",
        content: createAdminSelector?.message,
      });
      dispatch(createAdminAction.createAdminInfoReset());
    }
  }, [createAdminSelector, dispatch, messageApi, navigate]);

  useEffect(() => {
    if (updateAdminSelector?.data?.message) {
      messageApi.open({
        type: "success",
        content: updateAdminSelector?.data?.message,
      });
      dispatch(updateAdminAction.updateAdminInfoReset());
      navigate("/admins");
    } else if (updateAdminSelector?.message) {
      messageApi.open({
        type: "error",
        content: updateAdminSelector?.message,
      });
      dispatch(updateAdminAction.updateAdminInfoReset());
    }
  }, [updateAdminSelector, dispatch, messageApi, navigate]);

  return (
    <>
      {(createAdminSelector?.isLoading ||
        updateAdminSelector?.isLoading ||
        getAdminPermissionsSelector?.isLoading) && (
        <Loader loaderTransform="loaderTransform" />
      )}
      <div className="rightside">
        <div className="rightcontent">
          <p>
            <strong>{pageTitle}</strong>
          </p>
          <CustomBreadcrumb
            items={[
              { text: BreadCrum.HOME },
              {
                text: "Admin Management",
                onClick: () => navigate("/admins"),
              },
              { text: breadcrumbLabel, active: true },
            ]}
          />

          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={initialValues}
            validationSchema={
              isManageMode
                ? validationSchemaManageAdmin
                : validationSchemaAddAdmin
            }
            onSubmit={handleFormSubmit}
          >
            {({ values, errors, touched, setFieldValue, submitCount }) => {
              const isAllSelected =
                values.permissions.length === allPermissions.length &&
                allPermissions.every((permission) =>
                  values.permissions.includes(permission),
                );

              const handlePermissionToggle = (permission) => {
                const nextPermissions = values.permissions.includes(permission)
                  ? values.permissions.filter((item) => item !== permission)
                  : [...values.permissions, permission];

                setFieldValue("permissions", nextPermissions);
              };

              const handleSelectAll = () => {
                setFieldValue(
                  "permissions",
                  isAllSelected ? [] : [...allPermissions],
                );
              };

              const handleClearSelection = () => {
                setFieldValue("permissions", []);
              };

              return (
                <Form>
                  <div className="usercard mb-30">
                    <div className="campaignDetailsCard">
                      <span className="campaigncardHeader">
                        Admin Information{" "}
                        <span className="add-admin-required">*</span>
                      </span>
                    </div>
                    <hr className="m-0" />
                    <div className="campaigncardBody login-form">
                      <div className="form-group">
                        <label htmlFor="firstName">
                          First Name{" "}
                          {/* <span className="add-admin-required">*</span> */}
                        </label>
                        <Field
                          type="text"
                          id="firstName"
                          name="firstName"
                          className="form-control p-16"
                          placeholder="Enter First Name"
                          autoComplete="off"
                          onKeyPress={handleKeyPressSpace}
                        />
                        <ErrorMessage
                          name="firstName"
                          component="p"
                          className="error text-danger mt-1 mb-0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName">
                          Last Name{" "}
                          {/* <span className="add-admin-required">*</span> */}
                        </label>
                        <Field
                          type="text"
                          id="lastName"
                          name="lastName"
                          className="form-control p-16"
                          placeholder="Enter Last Name"
                          autoComplete="off"
                          onKeyPress={handleKeyPressSpace}
                        />
                        <ErrorMessage
                          name="lastName"
                          component="p"
                          className="error text-danger mt-1 mb-0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">
                          Email <span className="add-admin-required">*</span>
                        </label>
                        <Field
                          type="email"
                          id="email"
                          name="email"
                          className="form-control p-16"
                          placeholder="Enter Email"
                          autoComplete="off"
                          onKeyPress={handleKeyPressSpace}
                        />
                        <ErrorMessage
                          name="email"
                          component="p"
                          className="error text-danger mt-1 mb-0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="password">
                          Password{" "}
                          {/* {!isManageMode ? (
                            <span className="add-admin-required">*</span>
                          ) : null} */}
                        </label>
                        <div className="add-admin-password-field">
                          {isManageMode ? (
                            <input
                              type={showPassword ? "text" : "password"}
                              id="password"
                              name="password"
                              className="form-control p-16"
                              value={
                                passwordEdited
                                  ? storedPassword
                                  : MANAGE_ADMIN_PASSWORD_PLACEHOLDER
                              }
                              autoComplete="new-password"
                              onFocus={() => {
                                if (!passwordEdited) {
                                  setPasswordEdited(true);
                                  setStoredPassword("");
                                  setShowPassword(false);
                                  setFieldValue("password", "");
                                }
                              }}
                              onBlur={() => {
                                if (passwordEdited && !storedPassword.trim()) {
                                  setPasswordEdited(false);
                                  setShowPassword(false);
                                  setFieldValue("password", "");
                                }
                              }}
                              onChange={(event) => {
                                const nextPassword = event.target.value;
                                setStoredPassword(nextPassword);
                                setFieldValue("password", nextPassword);
                              }}
                            />
                          ) : (
                            <Field
                              type={showPassword ? "text" : "password"}
                              id="password"
                              name="password"
                              className="form-control p-16"
                              placeholder="Enter Password"
                              autoComplete="new-password"
                            />
                          )}
                          <button
                            type="button"
                            className="add-admin-password-toggle"
                            onClick={() => setShowPassword((prev) => !prev)}
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            <img
                              src={showPassword ? eyeIconClosed : eyeIcon}
                              alt=""
                              className="img24"
                            />
                          </button>
                        </div>
                        <ErrorMessage
                          name="password"
                          component="p"
                          className="error text-danger mt-1 mb-0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="status">Status</label>
                        <div className="add-admin-status-field">
                          <span className="add-admin-status-label">
                            {values.status}
                          </span>
                          <Switch
                            id="status"
                            className="switches"
                            checked={values.status === "Active"}
                            onChange={(checked) =>
                              setFieldValue(
                                "status",
                                checked ? "Active" : "Inactive",
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="role">Role</label>
                        <input
                          type="text"
                          id="role"
                          name="role"
                          className="form-control p-16"
                          value={values.role}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="usercard add-admin-permissions-card">
                    <div className="add-admin-permissions-header">
                      <h2 className="add-admin-permissions-title">
                        Permissions
                      </h2>
                      <div className="add-admin-permissions-actions">
                        <button
                          type="button"
                          className="add-admin-clear-selection"
                          onClick={handleClearSelection}
                        >
                          Clear Selection
                        </button>
                        <Checkbox
                          className="add-admin-select-all"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        >
                          Select All
                        </Checkbox>
                      </div>
                    </div>

                    <div className="add-admin-permissions-list">
                      {permissionGroups.map((group, index) => {
                        const isLastRow =
                          index === permissionGroups.length - 1;
                        const groupTitle = getPermissionGroupTitle(group);

                        return (
                          <div
                            key={groupTitle}
                            className={`add-admin-permission-row${
                              isLastRow ? " add-admin-permission-row-last" : ""
                            }`}
                          >
                            <p className="add-admin-permission-category">
                              {groupTitle}
                            </p>
                            <div className="add-admin-permission-options">
                              {group.permissions.map((permission) => {
                                const permissionValue =
                                  getPermissionValue(permission);

                                return (
                                  <Checkbox
                                    key={permissionValue}
                                    className="add-admin-permission-checkbox"
                                    checked={values.permissions.includes(
                                      permissionValue,
                                    )}
                                    onChange={() =>
                                      handlePermissionToggle(permissionValue)
                                    }
                                  >
                                    {getPermissionLabel(permission)}
                                  </Checkbox>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {(touched.permissions || submitCount > 0) &&
                  errors.permissions ? (
                    <p className="error text-danger mt-10 mb-0">
                      {errors.permissions}
                    </p>
                  ) : null}

                  <div className="d-flex justify-content-end gap-3 mb-5 mt-30">
                    <button
                      type="button"
                      className="py3 fw-18 gbo continuebtn savebtn cancelbtn me-20 transparentbtn"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="okbtn continuebtn">
                      Save
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default AddAdmin;
